import * as React from 'react'
import { useNavigate } from 'react-router-dom'
import { FaIcon, faShieldXmark } from '@/lib/fa'
import { useLocalizeSafe } from '@/modules/shared/context/localize/LocalizeContext'

export interface NoPermissionScreenProps {
  /** Heading. Defaults to the localized "No permission" title. */
  title?: string
  /** Body text. Defaults to the localized "you don't have permission" message. */
  description?: string
  /**
   * `page` (default) fills the content area and is centred; `inline` is a
   * compact card for gating a section/widget inside an already-populated page.
   */
  variant?: 'page' | 'inline'
  /** Optional name of the missing permission/role, shown as a hint. */
  missingPermission?: string
  /** Show a "Go back" button (default true for `page`, false for `inline`). */
  showBack?: boolean
  /** Optional href for a primary "Go to dashboard"-style action. */
  homeHref?: string
  className?: string
}

/**
 * "You don't have permission for this action" screen. Localized via the
 * `shared.noPermission.*` keys with Polish fallbacks, so it renders correctly
 * even before translation entries exist.
 */
export const NoPermissionScreen: React.FC<NoPermissionScreenProps> = ({
  title,
  description,
  variant = 'page',
  missingPermission,
  showBack,
  homeHref,
  className,
}) => {
  const navigate = useNavigate()
  const { tSafe } = useLocalizeSafe()
  const t = (key: string, fallback: string) =>
    tSafe(`shared.noPermission.${key}`, fallback)

  const resolvedTitle = title ?? t('title', 'Brak uprawnień')
  const resolvedDescription =
    description ??
    (variant === 'page'
      ? t('pageDescription', 'Nie masz uprawnień do wyświetlenia tej strony.')
      : t('description', 'Nie masz uprawnień do wykonania tej akcji.'))

  const canGoBack = showBack ?? variant === 'page'

  const container =
    variant === 'page'
      ? 'tw-flex tw-h-full tw-min-h-[320px] tw-w-full tw-flex-1 tw-items-center tw-justify-center tw-p-6'
      : 'tw-flex tw-w-full tw-items-center tw-justify-center tw-p-4'

  return (
    <div role="alert" className={`${container} ${className ?? ''}`}>
      <div className="tw-flex tw-max-w-md tw-flex-col tw-items-center tw-text-center">
        <div
          aria-hidden="true"
          className="tw-mb-4 tw-flex tw-h-14 tw-w-14 tw-items-center tw-justify-center tw-rounded-full tw-bg-base-200 tw-text-primary"
        >
          <FaIcon icon={faShieldXmark} className="tw-h-6 tw-w-6" />
        </div>

        <h2 className="tw-text-lg tw-font-semibold tw-text-content-base">
          {resolvedTitle}
        </h2>
        <p className="tw-mt-2 tw-text-sm tw-text-content-secondary">
          {resolvedDescription}
        </p>

        {missingPermission && (
          <p className="tw-mt-2 tw-text-xs tw-text-content-secondary tw-opacity-70">
            {t('missing', 'Wymagane uprawnienie')}: {missingPermission}
          </p>
        )}

        <p className="tw-mt-3 tw-text-xs tw-text-content-secondary tw-opacity-70">
          {t(
            'contactAdmin',
            'Skontaktuj się z administratorem firmy, aby uzyskać dostęp.',
          )}
        </p>

        {(canGoBack || homeHref) && (
          <div className="tw-mt-6 tw-flex tw-flex-wrap tw-items-center tw-justify-center tw-gap-3">
            {canGoBack && (
              <button
                type="button"
                onClick={() => navigate(-1)}
                className="tw-inline-flex tw-items-center tw-justify-center tw-rounded-lg tw-border tw-border-base-300 tw-bg-base-100 tw-px-4 tw-py-2 tw-text-sm tw-font-medium tw-text-content-base tw-transition-colors hover:tw-bg-base-200"
              >
                {t('back', 'Wróć')}
              </button>
            )}
            {homeHref && (
              <a
                href={homeHref}
                className="tw-inline-flex tw-items-center tw-justify-center tw-rounded-lg tw-bg-primary tw-px-4 tw-py-2 tw-text-sm tw-font-medium tw-text-white tw-transition-opacity hover:tw-opacity-90"
              >
                {t('goToDashboard', 'Przejdź do pulpitu')}
              </a>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
